import { useState, useEffect, useRef } from 'react';

interface UseCountUpOptions {
  target: number;
  duration?: number;
  delay?: number;
  format?: 'number' | 'currency' | 'percent' | 'compact';
  decimals?: number;
  prefix?: string;
  suffix?: string;
  enabled?: boolean;
}

export function useCountUp({
  target,
  duration = 2000,
  delay = 0,
  format = 'number',
  decimals = 0,
  prefix = '',
  suffix = '',
  enabled = true,
}: UseCountUpOptions) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    if (!enabled) return;

    mountedRef.current = true;
    const startTimeout = setTimeout(() => {
      if (!mountedRef.current) return;
      startTimeRef.current = performance.now();

      const animate = (now: number) => {
        if (!mountedRef.current) return;
        const elapsed = now - startTimeRef.current;
        const progress = Math.min(elapsed / duration, 1);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = eased * target;
        setDisplay(current);

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          setDisplay(target);
        }
      };

      rafRef.current = requestAnimationFrame(animate);
    }, delay);

    return () => {
      mountedRef.current = false;
      clearTimeout(startTimeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration, delay, enabled]);

  const formatValue = (val: number): string => {
    let formatted = '';
    const fixed = val.toFixed(decimals);

    switch (format) {
      case 'currency':
        formatted = Number(fixed).toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
        break;
      case 'percent':
        formatted = fixed + '%';
        break;
      case 'compact':
        if (val >= 1e9) formatted = (val / 1e9).toFixed(1) + 'B';
        else if (val >= 1e6) formatted = (val / 1e6).toFixed(1) + 'M';
        else if (val >= 1e3) formatted = (val / 1e3).toFixed(1) + 'K';
        else formatted = fixed;
        break;
      default:
        formatted = Number(fixed).toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
    }

    return `${prefix}${formatted}${suffix}`;
  };

  return { display, formatted: formatValue(display) };
}